import { useState, useEffect } from 'react';

interface Task {
  id: string;
  title: string;
  value: number;
  paid: boolean;
  billing_category?: string;
}

interface FinancialDetailsModalProps {
  task: Task;
  onClose: () => void;
  onSave: (data: {
    value: number;
    payment_date: string;
    payment_method: string;
    financial_notes: string;
  }) => void;
}

const PAYMENT_METHODS = [
  { value: 'pix', label: 'PIX' },
  { value: 'credit_card', label: 'Cartão de Crédito' },
  { value: 'debit_card', label: 'Cartão de Débito' },
  { value: 'boleto', label: 'Boleto' },
  { value: 'bank_transfer', label: 'Transferência Bancária' },
  { value: 'cash', label: 'Dinheiro' },
];

const BILLING_CATEGORIES = [
  { value: 'Desenvolvimento', label: 'Desenvolvimento' },
  { value: 'Bugfix', label: 'Correção de Bug' },
  { value: 'Consultoria', label: 'Consultoria' },
  { value: 'Suporte', label: 'Suporte Técnico' },
  { value: 'Design', label: 'Design/UI' },
  { value: 'Infraestrutura', label: 'Infraestrutura' },
  { value: 'Treinamento', label: 'Treinamento' },
  { value: 'Outro', label: 'Outro' },
];

export default function FinancialDetailsModal({ task, onClose, onSave }: FinancialDetailsModalProps) {
  const [value, setValue] = useState(task.value.toString());
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMethod, setPaymentMethod] = useState('pix');
  const [financialNotes, setFinancialNotes] = useState('');
  const [category, setCategory] = useState(task.billing_category || 'Desenvolvimento');

  useEffect(() => {
    if (task.value > 0) {
      setValue(task.value.toString());
    }
  }, [task.value]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const numValue = parseFloat(value) || 0;
    
    onSave({
      value: numValue,
      payment_date: paymentDate,
      payment_method: paymentMethod,
      financial_notes: financialNotes,
    });
  };

  const isEditing = task.value > 0 && !task.paid;
  const isMarkingPaid = task.value > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-slate-800 font-semibold text-base">
              {task.paid ? 'Detalhes Financeiros' : isEditing ? 'Definir Pagamento' : 'Orçar e Receber'}
            </h2>
            <p className="text-slate-400 text-xs mt-0.5 truncate max-w-[250px]">{task.title}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Valor */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              {task.paid ? 'Valor pago' : 'Valor (R$)'}
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">R$</span>
              <input
                type="number"
                min={0}
                step={0.01}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="0,00"
                disabled={task.paid}
                className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300 disabled:bg-slate-50 disabled:text-slate-400"
                required
              />
            </div>
          </div>

          {/* Categoria */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Categoria</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              disabled={task.paid}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-300 cursor-pointer disabled:bg-slate-50 disabled:text-slate-400"
            >
              {BILLING_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          {!task.paid && (
            <>
              {/* Data do pagamento */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Data do pagamento</label>
                <input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                  required
                />
              </div>

              {/* Forma de pagamento */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Forma de pagamento</label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-300 cursor-pointer"
                >
                  {PAYMENT_METHODS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>

              {/* Observações financeiras */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Observações</label>
                <textarea
                  value={financialNotes}
                  onChange={(e) => setFinancialNotes(e.target.value)}
                  rows={2}
                  maxLength={500}
                  placeholder="Notas sobre o pagamento..."
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300 resize-none"
                />
              </div>

              {/* Info sobre crédito automático */}
              <div className="bg-emerald-50 rounded-lg p-3 flex items-start gap-2">
                <i className="ri-information-line text-emerald-500 mt-0.5"></i>
                <p className="text-xs text-emerald-700">
                  Ao confirmar, será gerado um <strong>crédito automático no caixa</strong> no valor informado.
                </p>
              </div>
            </>
          )}

          {task.paid && (
            <div className="bg-emerald-50 rounded-lg p-4 text-center">
              <i className="ri-checkbox-circle-fill text-emerald-500 text-2xl mb-2"></i>
              <p className="text-sm font-medium text-emerald-700">Pagamento confirmado</p>
              <p className="text-xs text-emerald-600 mt-1">Crédito gerado no caixa</p>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            {task.paid ? 'Fechar' : 'Cancelar'}
          </button>
          {!task.paid && (
            <button
              onClick={handleSubmit}
              disabled={!value || parseFloat(value) <= 0}
              className="px-5 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-checkbox-circle-line mr-1.5"></i>
              Confirmar Pagamento
            </button>
          )}
        </div>
      </div>
    </div>
  );
}